import { parseISO } from "date-fns";
import { Culture, regionIdToCulture } from "../../Contract/Culture";
import { RegionId } from "../../Contract/RegionId";
import { getCurrency } from "../currency/getCurrency";

export const numberFormatter =
  (culture?: Culture | Culture[]) =>
  (number?: number, showDecimals: boolean = false): string => {
    if (number === undefined) {
      return "";
    }

    return new Intl.NumberFormat(culture, {
      style: "decimal",
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0,
    }).format(number);
  };

export const getNumberFormatGroupSeparator = (locale: Culture) => {
  const formatter = new Intl.NumberFormat(locale);
  const parts = formatter.formatToParts(1234566.89);
  const group = parts.find((part) => part.type === "group");
  return group?.value;
};

export const currencyFormatter =
  (showDecimals = false) =>
  (number: number | undefined, culture: RegionId | Culture | undefined): string => {
    if (number === undefined) {
      return "";
    }
    if (typeof culture === "number") culture = regionIdToCulture(culture);

    if (culture === undefined) return "";

    const currency = getCurrency(culture);

    if (number === undefined) {
      return "";
    }

    const digits = showDecimals ? 2 : 0;

    return new Intl.NumberFormat(culture, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(number);
  };

export const dateFormatter =
  (culture?: Culture) =>
  (date: Date, options?: Intl.DateTimeFormatOptions): string => {
    if (date === undefined) {
      return "";
    }

    return new Intl.DateTimeFormat(culture, options).format(new Date(date));
  };

export const parseDateIfString = (date: Date | string) => (typeof date == "string" ? parseISO(date) : date);

export const accountNumberFormatter =
  (region: RegionId) =>
  (accountNumber?: string): string => {
    if (accountNumber === undefined) {
      return "";
    }

    if (region == RegionId.Norway) {
      if (accountNumber.length != 11) return accountNumber;
      return `${accountNumber.slice(0, 4)}.${accountNumber.slice(4, 6)}.${accountNumber.slice(6)}`;
    }
    return accountNumber;
  };

/**
 * Quickly replace "{someVariable}" in a string
 *
 * @example
 * const string = "Hello {firstName}, how are you this {weekday}?";
 * formatString(
 *   string,
 *   {
 *     firstName: "Ola",
 *     weekday: "thursday"
 *   }
 * )
 * // -> Returns "Hello Ola, how are you this thursday?"
 */
export function formatString(str: string, values: Record<string, any> = {}) {
  return Object.keys(values).reduce((prev, key) => prev.replace(new RegExp(`{${key}}`, "g"), values[key]), str);
}
